import { supabase } from "@/integrations/supabase/client";
import { logErrorToConsole } from "@/utils/errorHandlerUtils";
import type {
  RealtimeChannel,
  RealtimePostgresChangesPayload,
  RealtimePostgresInsertPayload,
} from "@supabase/supabase-js";

export class RealtimeService {

  /**
   * Subscribe a channel to start receiving events
   */
  static async subscribeChannel(channel: RealtimeChannel): Promise<void> {
    try {
      await channel.subscribe();
    } catch (error) {
      logErrorToConsole(error, { 
        module: 'RealtimeService', 
        operation: 'subscribeChannel'
      });
      throw error;
    }
  }

  /**
   * Unsubscribe and remove a channel
   */
  static async unsubscribeChannel(channel: RealtimeChannel): Promise<void> {
    try {
      await supabase.removeChannel(channel);
    } catch (error) {
      logErrorToConsole(error, {
        module: 'RealtimeService',
        operation: 'unsubscribeChannel'
      });
      // Don't throw here as this is usually called in cleanup
    }
  }

  /**
   * Create a subscription for all changes to a table
   */
  static createTableSubscription(
    table: string,
    onChanges: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void,
    filter?: { event?: '*' | 'INSERT' | 'UPDATE' | 'DELETE'; filter?: string }
  ): RealtimeChannel {
    try {
      const event = (filter?.event || '*') as '*';
      const channelConfig: { event: '*'; schema: string; table: string; filter?: string } = {
        event,
        schema: "public",
        table,
      };

      if (filter?.filter) {
        channelConfig.filter = filter.filter;
      }

      const channel = supabase
        .channel(`${table}_changes`)
        .on(
          "postgres_changes",
          channelConfig,
          onChanges
        );

      return channel;
    } catch (error) {
      logErrorToConsole(error, {
        module: 'RealtimeService',
        operation: 'createTableSubscription',
        details: { table, filter }
      });
      throw error;
    }
  }

  /**
   * Create an optimized readings subscription filtered by sensor IDs
   */
  static createReadingsSubscription(
    sensorIds: string[],
    onNewReading: (payload: RealtimePostgresInsertPayload<Record<string, unknown>>) => void
  ): RealtimeChannel {
    try {
      const channelName = `readings-for-sensors-${sensorIds.join('-')}`;
      // El filtro realtime `in` espera valores separados por coma SIN comillas:
      // `in.(a,b,c)`. Los sensor_id son strings alfanuméricos con guiones bajos
      // (sin comas ni espacios), así que no requieren quoting; añadirlo haría que
      // las comillas formaran parte del valor y el filtro no matchearía.
      const filter = `sensor_id=in.(${sensorIds.join(',')})`;

      const channel = supabase
        .channel(channelName)
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "readings",
            filter,
          },
          onNewReading
        );

      return channel;
    } catch (error) {
      logErrorToConsole(error, {
        module: 'RealtimeService',
        operation: 'createReadingsSubscription',
        details: { sensorIds }
      });
      throw error;
    }
  }
}